import { ExternalIntegrationProvider } from "@bbj-types/externalIntegrations";

export enum ExternalIntegrationErrorLinkAction {
  OPEN_INTERVIEW_EXIT = "openInterviewExit",
}

export interface IExternalIntegrationErrorMessage {
  message: string;
  solution?: string;
  link?: { label: string; action: ExternalIntegrationErrorLinkAction };
  retry?: boolean;
}

const NEOLINK_ERROR_MESSAGES: Array<{ pattern: RegExp } & IExternalIntegrationErrorMessage> = [
  {
    pattern: /cr[ée]neau.*d[ée]j[àa] (pris|occup[ée])/i,
    message: "Un rendez-vous existe déjà dans Neolink pour cet allocataire sur ce créneau.",
    solution: "Supprimez ce rendez-vous dans Neolink ou modifiez le créneau de l'action dans Horizon.",
  },
  {
    pattern: /identifiant de rendez-vous n'existe pas/i,
    message: "Ce rendez-vous n'existe plus dans Neolink.",
    solution: "Si vous souhaitez qu'il réapparaisse dans Neolink, veuillez le supprimer puis le récréer dans Horizon.",
  },
  {
    pattern: /allocataire fourni n'est pas suivi/i,
    message: "L'allocataire n'est pas suivi par votre structure dans Neolink.",
    solution:
      "Vérifiez que le candidat est toujours bénéficiaire du RSA et domicilié dans votre périmètre géographique. Si c'est le cas, contactez le support pour régulariser cette erreur.",
  },
  {
    pattern: /dernier contrat.*en_cours/i,
    message: "Un CE est déjà en cours pour cet allocataire dans Neolink.",
    solution: "Clôturez le CE via l'entretien de sortie puis réessayez l'envoi via le bouton ci-dessous.",
    link: { label: "l'entretien de sortie", action: ExternalIntegrationErrorLinkAction.OPEN_INTERVIEW_EXIT },
    retry: true,
  },
  {
    pattern: /dernier contrat.*attente_signature_allocataire/i,
    message: "Un contrat est en attente de signature du candidat dans Neolink.",
    solution:
      "Ce contrat n'a pas été créé depuis Horizon : clôturez-le directement dans Neolink, puis réalisez la clôture via l'entretien de sortie en autonomie.",
    link: { label: "l'entretien de sortie", action: ExternalIntegrationErrorLinkAction.OPEN_INTERVIEW_EXIT },
    retry: true,
  },
  {
    pattern: /dernier contrat.*en_attente_validation/i,
    message: "Un contrat est en attente de validation dans Neolink.",
    solution:
      "Ce contrat n'a pas été créé depuis Horizon : demandez à un manager de le valider dans Neolink, puis réalisez la clôture via l'entretien de sortie en autonomie.",
    link: { label: "l'entretien de sortie", action: ExternalIntegrationErrorLinkAction.OPEN_INTERVIEW_EXIT },
    retry: true,
  },
  {
    pattern: /[ée]tat 'attente_signature_allocataire'/i,
    message: "Un contrat est en attente de signature du candidat dans Neolink.",
    solution:
      "Ce contrat n'a pas été créé depuis Horizon : clôturez-le directement dans Neolink, puis réalisez la clôture via le processus décrit dans le détail de l'erreur du CE concerné.",
  },
  {
    pattern: /[ée]tat 'en_attente_validation'/i,
    message: "Un contrat est en attente de validation dans Neolink.",
    solution:
      "Ce contrat n'a pas été créé depuis Horizon : demandez à un manager de le valider dans Neolink, puis réalisez la clôture via le processus décrit dans le détail de l'erreur du CE concerné.",
  },
  {
    pattern: /[ée]tat 'cloture'/i,
    message: "Clôturé dans Neolink",
  },
  {
    pattern: /idAllocataire/i,
    message: "L'identifiant allocataire est manquant ou invalide.",
    solution: "Veuillez contacter votre administrateur Horizon.",
  },
  {
    pattern: /emailReferent fourni|email du r[ée]f[ée]rent fourni/i,
    message: "L'email du référent est inconnu de Neolink.",
    solution:
      "Vérifiez que l'adresse email renseignée dans votre profil Horizon est bien celle disposant des droits d'accès dans Neolink.",
  },
];

// Raw errors embed the whole failed call (URL, echoed request body, status, response). Only the provider
// response is meaningful: the echoed request would match any field name used in the patterns.
const getProviderResponse = (error: string) => {
  const responseIndex = error.indexOf("Response:");
  return responseIndex === -1 ? error : error.slice(responseIndex + "Response:".length);
};

export const getExternalIntegrationErrorMessage = (
  provider: ExternalIntegrationProvider,
  error: string | undefined
): IExternalIntegrationErrorMessage | undefined => {
  if (provider !== ExternalIntegrationProvider.NEOLINK || !error) {
    return undefined;
  }
  const response = getProviderResponse(error);
  const match = NEOLINK_ERROR_MESSAGES.find(({ pattern }) => pattern.test(response));
  if (!match) {
    return undefined;
  }
  const { pattern: _pattern, ...errorMessage } = match;
  return errorMessage;
};
